import { Link } from 'react-router-dom';
import { INSTAGRAM_URL, MAPS_URL, WHATSAPP_URL } from '@/data/constants';
import { InstagramIcon, WhatsAppIcon } from '@/components/icons';

const HOURS = [
  { day: 'Mon – Fri',  hours: '5:45AM – 10PM', style: 'text-white' },
  { day: 'Saturday',   hours: '7AM – 3PM',    style: 'text-[#14B8A6]' },
  { day: 'Sunday',     hours: 'Closed',      style: 'text-red-400' },
];

const NAV_LINKS: { id: string; label: string; to?: string }[] = [
  { id: 'classes',       label: 'Classes' },
  { id: 'schedule',      label: 'Schedule' },
  { id: 'coaches',       label: 'Coaches' },
  { id: 'physiotherapy', label: 'Physiotherapy' },
  { id: 'membership',    label: 'Membership' },
  { id: 'events',        label: 'Events', to: '/events' },
  { id: 'about',         label: 'About Us' },
  { id: 'contact',       label: 'Contact' },
];

export default function Footer() {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#080808] border-t border-white/[0.06] pt-14 lg:pt-20 pb-8 px-4 lg:px-8 xl:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Top grid — 5 columns on lg (brand takes 2) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8 mb-14">

          {/* Brand — 2 columns on lg */}
          <div className="sm:col-span-2 lg:col-span-2">
            <button
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-3 mb-4 group cursor-pointer"
            >
              <img
                src="assets/hub30Logo.jpg"
                alt="HUB30 gym logo — Jbeil, Lebanon"
                loading="lazy"
                decoding="async"
                className="w-12 h-12 object-cover rounded-full border-2 border-[#14B8A6]/60 group-hover:border-[#14B8A6] transition-colors"
              />
              <span className="text-white text-2xl font-bold tracking-widest group-hover:text-[#14B8A6] transition-colors">
                HUB30
              </span>
            </button>

            <p className="text-[#14B8A6] text-xs font-medium tracking-wide mb-3">
              Train Hard. Recover Smart.
            </p>
            <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-xs">
              A performance training center for serious athletes. Strength, calisthenics, and functional fitness — all under one roof.
            </p>

            {/* Social Icons */}
            <div className="flex gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#14B8A6] hover:bg-[#14B8A6]/10 transition-all duration-200 cursor-pointer"
                aria-label="Instagram"
              >
                <InstagramIcon className="text-lg" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-[#25D366] hover:bg-[#25D366]/10 transition-all duration-200 cursor-pointer"
                aria-label="WhatsApp"
              >
                <WhatsAppIcon className="text-lg" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <nav aria-label="Footer navigation">
            <h4 className="text-white text-xs tracking-widest font-semibold mb-5 uppercase">Navigate</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.map(({ id, label, to }) => (
                <li key={id}>
                  {to ? (
                    <Link
                      to={to}
                      className="text-gray-500 text-sm hover:text-[#14B8A6] transition-colors cursor-pointer"
                    >
                      {label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollToSection(id)}
                      className="text-gray-500 text-sm hover:text-[#14B8A6] transition-colors cursor-pointer"
                    >
                      {label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Location + Contact */}
          <div>
            <h4 className="text-white text-xs tracking-widest font-semibold mb-5 uppercase">Location</h4>
            <address className="not-italic">
              <p className="text-gray-400 text-sm mb-2 leading-relaxed">Jbeil (Byblos), Lebanon</p>
            </address>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#14B8A6] text-xs font-medium hover:underline mb-6"
            >
              Get Directions <i className="ri-arrow-right-line" />
            </a>

            <h4 className="text-white text-xs tracking-widest font-semibold mb-3 uppercase">Contact</h4>
            <address className="not-italic">
              <div className="space-y-2.5">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-500 hover:text-[#25D366] transition-colors text-sm"
                >
                  <WhatsAppIcon className="text-base" />
                  +961 71 077 545
                </a>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-500 hover:text-[#14B8A6] transition-colors text-sm"
                >
                  <InstagramIcon className="text-base" />
                  @hub30lb
                </a>
              </div>
            </address>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-white text-xs tracking-widest font-semibold mb-5 uppercase">Hours</h4>
            <div className="space-y-3">
              {HOURS.map(({ day, hours, style }) => (
                <div key={day} className="flex flex-col gap-0.5">
                  <span className="text-gray-500 text-xs">{day}</span>
                  <span className={`text-sm font-medium ${style}`}>{hours}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/[0.06] mb-6" />

        {/* Copyright */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <p className="text-gray-600 text-xs">© 2026 HUB30. All rights reserved.</p>
          <p className="text-gray-700 text-xs">Jbeil, Lebanon</p>
        </div>
      </div>
    </footer>
  );
}
